// ─────────────────────────────────────────────────────────────────────────────
// netti future — the next-generation innernet organism.
//
// A from-scratch, single-pass WebGL2 renderer: the WHOLE organism (cell, glass
// petals, wing venation, node cells, motes, glow) is computed per-pixel in one
// fullscreen fragment shader over procedural polar space. There is no geometry,
// no instancing, no FBO blur chain — glow is ANALYTIC (exp(-d)) straight from
// the distance fields, and every petal derives from its index via hash, so the
// organism is infinitely variable from a seed and a handful of tuners.
//
// THE FORMATION SPACE (the modular insight): petal i sits at
//     angle  = i·α + spin·(differential)      radius = c·(i/N)^p
// One (α, p) pair sweeps the whole reference board — golden-angle α is a
// sunflower/dandelion burst, α ≈ 2π/m is a pincushion of spokes, α = 2π/m + δ
// twists into dahlia rosettes, and a small α winds the spiral-staircase worm.
//
// THE MISTAKES LEDGER, satisfied BY CONSTRUCTION (learned from the warm coil):
//  • never-clip     — extent is analytic (reach + petal length + glow pad); the
//                     world scale fits it inside the canvas before drawing.
//  • rooted petals  — petal bases are DEFINED at the cell rim + tucked under it;
//                     there is no gap to patch because geometry starts there.
//  • no boxy quads  — no quads at all; SDFs evaluated in continuous space.
//  • no HDR blowout — one hue-preserving max-channel knee at the very end;
//                     no additive FBO accumulation anywhere.
//  • no hue tearing — no 8-bit intermediate targets; everything stays in
//                     shader registers until the final composite.
//  • organic spin   — differential (outer petals travel further), angular only,
//                     spring-driven with shortest-path targets on the CPU.
//  • transparent    — premultiplied straight out of the shader; the alpha IS the
//                     organism envelope, so exports carry true per-pixel alpha
//                     and the canvas composites over any ground with no mask.
//  • memory law     — nothing time-grows; age is set from outside (cells).
// ─────────────────────────────────────────────────────────────────────────────

import type { ColorState, DerivedPalette, HueSL, RGB } from "../color";
import { derivePalette, DEFAULT_COLOR } from "../color";
import { CELL_GLSL } from "../../brand/cell";
import { groundDarkness } from "../groundLaw";

/** Every dial the future organism understands. All 0..1 unless noted. */
export type FutureTuners = {
  /** Formation morph: 0 = spiral staircase (worm) → 0.5 = twisted rosette →
   *  1 = golden-angle burst (sunflower/dandelion). Blends α through the lattice. */
  formation: number;
  /** Radial growth exponent shape: 0 = tight centre / long tail, 1 = even ring. */
  radialBias: number;
  /** How many petals at full age (scaled by age law). */
  abundance: number;
  /** Petal length. */
  petalLength: number;
  /** Petal width. */
  petalWidth: number;
  /** Tip sharpness: 0 = round bulb → 1 = pointed blade. */
  tipSharpness: number;
  /** Petal bend/curvature along its length (sickle). */
  bend: number;
  /** Per-petal irregularity (length/width/angle jitter — the "alive" scatter). */
  variety: number;
  /** Venation visibility/density inside petals. */
  venation: number;
  /** Node cells at vein endpoints (dandelion orbs). */
  nodes: number;
  /** Glass translucency of the membrane. */
  glass: number;
  /** Inner light / bioluminescence strength. */
  innerLight: number;
  /** Undulation amplitude (the worm swim). */
  sway: number;
  /** Undulation tempo. */
  tempo: number;
  /** Continuous revolve speed — 0 still · 1 a slow perpetual 360°+ rotation.
   *  Rides the spin spring, so outer petals trail with real momentum. */
  revolve: number;
  /** Breath depth (slow radial swell). */
  breath: number;
  /** Drifting luminous motes (count / presence). */
  motes: number;
  /** Mote size — 0 fine dust → 1 soft orbs. */
  moteSize: number;
  /** Pointer reactivity — petals scintillate near the cursor (no warp). */
  touch: number;
  /** Central cell size relative to reach. */
  cellSize: number;
  /** Cell rim wobble (organic squircle irregularity). */
  cellWobble: number;
  /** Hue spread across petals (how far the palette fans out). */
  hueSpread: number;
  /** THE CELL AS AN ORB OF LIGHT (0…1, default 0). A cell on its own — the
   *  birth's resting cell, before any petals — reads as a pearl with a darker
   *  limb and a thin coloured rim; at 1 the limb darkening is gone, the pearl
   *  whitens out to its edge and throws a soft halo past it. 0 leaves every
   *  netti exactly as it was. */
  cellGlow: number;
  /** HOW LIT THE CORE IS (0…1, default 1). Only the pearl, the halo past its
   *  rim, the rim light and the birth glow answer to it; the petals, the veins
   *  and the ground never do, at any value. 1 is netti exactly as it has always
   *  been (the math reduces to today's, bit for bit); falling toward 0.3 the
   *  pearl dims into a shaded bead and its light draws in with it, and below
   *  that it only gets quieter. It is a STATE a surface drives (a netti that can
   *  not yet see wears a dim core, and brightens as it is given sight), never an
   *  identity: it is not minted, and it stays LAST in this list so no person's
   *  draw order moves (see futureTunersFromSeed). */
  cellLight: number;
};

export const FUTURE_TUNER_DEFAULTS: FutureTuners = {
  formation: 0.62,
  radialBias: 0.45,
  abundance: 0.55,
  petalLength: 0.55,
  petalWidth: 0.5,
  tipSharpness: 0.45,
  bend: 0.35,
  variety: 0.55,
  venation: 0.44,
  nodes: 0.42,
  glass: 0.55,
  innerLight: 0.6,
  sway: 0.5,
  tempo: 0.45,
  revolve: 0.3,
  breath: 0.4,
  motes: 0.5,
  moteSize: 0.35,
  touch: 0.5,
  cellSize: 0.3,
  cellWobble: 0.5,
  hueSpread: 0.6,
  cellGlow: 0,
  // LAST, always: futureTunersFromSeed draws one number per dial in this order,
  // so a dial appended here shifts nobody who was minted before it existed.
  cellLight: 1,
};

// ── MINTING — the tuner bands a real user's organism may be born into ────────
// Deterministic per-seed variation, constrained to the bands that ALWAYS render
// beautifully. Two dials are deliberately excluded from extremes: cellSize is
// never minted high and radialBias never low — those combinations squeeze the
// whorl roots around a big cell and expose the petal bases. Motion dials
// (sway/tempo/revolve/breath/particles/touch) stay at product defaults — they
// are behaviour, not identity.
//
// WIDENED (2026-09-02) — two people's organisms differed mostly in COLOUR; the
// bodies were near-twins because every band sat in the middle third of its dial.
// The bands now span most of each dial, so form varies as much as palette does:
// blade-thin petals vs broad ones, tight rosettes vs golden bursts, glassy vs
// dense, sparse venation vs a full lattice. The two safety rules are untouched
// (cellSize never high, radialBias never low) because those are the two
// combinations that expose the petal roots. Motion dials stay out of the mint
// entirely — they are behaviour, not identity.
//
// ⚠️ This re-maps seed → shape, so existing organisms change form once (their
// COLOUR, minted by a different function, is unchanged). One settling, then
// stable forever after.
export type FutureMintBands = Partial<Record<keyof FutureTuners, [number, number]>>;

export const FUTURE_MINT_BANDS: FutureMintBands = {
  formation: [0.30, 0.95],
  radialBias: [0.42, 0.82],   // never low — roots stay layered, not crammed
  abundance: [0.34, 0.86],
  petalLength: [0.34, 0.80],
  petalWidth: [0.26, 0.78],
  tipSharpness: [0.04, 0.74],
  bend: [0.08, 0.78],
  variety: [0.26, 0.82],
  venation: [0.18, 0.72],
  nodes: [0.16, 0.72],
  glass: [0.32, 0.84],
  innerLight: [0.40, 0.88],
  cellSize: [0.12, 0.34],     // never high — the pill stays a heart, not a wall
  cellWobble: [0.16, 0.76],
  hueSpread: [0.30, 0.90],
};

/** Deterministic mint-safe tuners for a user seed — the canonical calculation
 *  a real minted organism is born from. Same seed, same organism, forever.
 *
 *  `bands` is the live mint when the tuner has moved it off the code's bands
 *  (migration 111, `netti_mint_bands`). THE DRAW ORDER IS FIXED: every canonical
 *  dial draws its number in the code's order whether or not it is still open, so
 *  closing one dial never shifts the draw of the dials after it; a dial opened
 *  later draws after all of them, in FutureTuners order. A NEW dial therefore
 *  goes at the END of FutureTuners and FUTURE_TUNER_DEFAULTS (cellLight did),
 *  and stays out of FUTURE_MINT_BANDS unless it is meant to be identity:
 *  tuner.test.ts pins the draws of every dial that came before it. */
export function futureTunersFromSeed(seed: number, bands: FutureMintBands = FUTURE_MINT_BANDS): Partial<FutureTuners> {
  const rng = lcg(seed >>> 0 || 1);
  const out: Partial<FutureTuners> = {};
  const canon = Object.keys(FUTURE_MINT_BANDS) as (keyof FutureTuners)[];
  for (const key of canon) {
    const r = rng();
    const band = bands[key];
    if (band) out[key] = band[0] + r * (band[1] - band[0]);
  }
  for (const key of Object.keys(FUTURE_TUNER_DEFAULTS) as (keyof FutureTuners)[]) {
    if (canon.includes(key)) continue;
    const r = rng();
    const band = bands[key];
    if (band) out[key] = band[0] + r * (band[1] - band[0]);
  }
  return out;
}

export type FutureOrganismOptions = {
  canvas: HTMLCanvasElement;
  color?: ColorState;
  seed?: number;
  /** Ground the organism composites over (used for absorptive tinting math; the
   *  output itself is premultiplied-transparent). */
  bg?: RGB;
  /** 0..1 — dark grounds flip the compositor toward bioluminescence. Derived
   *  from bg luma when omitted. */
  maxDpr?: number;
  tuners?: Partial<FutureTuners>;
  /** Memory age 0..1 — drives petal count + reach (the memory law). */
  age?: number;
  /** how much of the paper creature to keep on a dark ground (setPresence) */
  presence?: number;
  reducedMotion?: boolean;
  /** Frame-rate ceiling (0 = uncapped rAF). The organism is a slow ambient
   *  bloom — 30fps is visually identical and HALVES the GPU load, which is the
   *  difference between a phone scrolling at 60fps and one that stutters. */
  fpsCap?: number;
  /** Keep the drawing buffer after present. Only the ASCII veil (which samples
   *  this canvas per frame) needs it; everywhere else `false` lets tiled mobile
   *  GPUs skip a full framebuffer copy every frame. Default true (legacy). */
  preserveDrawingBuffer?: boolean;
};

type FirePulse = { petal: number; born: number; speed: number };

const TAU = Math.PI * 2;
// float32 hygiene for the two accumulators that ride to the GPU (see step() and
// drawScene()). 4π: the mote term reads uSpin*0.5. 500·4π ≈ 6283 s: an exact
// multiple of 2π/0.5 (×500) and of 2π/2.2 (×2200), the shader's fixed clocks.
const SPIN_WRAP = 2 * TAU;
const TIME_WRAP = 500 * 2 * TAU;

// REST MODE — what netti does while a frost covers it (2026-09-26 rework).
//
// It used to drop to 12fps and step its resolution down through three tiers as
// the frost settled. Both showed. Behind a live backdrop blur a 12fps creature
// visibly JUDDERS (the blur re-runs only on the frames netti draws, so the
// whole frosted room ticked like a slideshow), a fps that eased continuously
// between 60 and 12 landed on uneven beats (45fps on a 60Hz display alternates
// one and two refreshes: a stutter while idling), and every tier change
// re-allocated the canvas mid-fade, where the frost was still thin enough to
// show the step. That was the flash-and-jitter behind the sheets.
//
// Now it trades PIXELS, not frames:
//   · fps holds an EVEN beat: the lit rate, or exactly half of it once the
//     frost is more than half settled, never anything in between.
//   · resolution drops hard (to REST_DPR device px per css px, about a sixth
//     of the pixels at dpr 1, a twenty-fifth at dpr 2), but ONLY once the
//     frost has fully settled over it, where the blur swallows the upscale
//     whole, and it comes back to full on the very frame the frost starts to
//     lift, before a single thinner-frost frame is presented. Nobody ever sees
//     the small version; the GPU just stops paying for pixels behind a blur.
const REST_DPR = 0.4;
// the middle tier: a page that opens partly frosted (the reading floor) or a
// frost on its way in. Soft enough that the half-frost hides it, sharp enough
// that the half that shows never reads as low resolution.
const REST_MID_DPR = 0.8;
const REST_MID_AT = 0.45;
/** the frost must be at least this settled before the resolution lets go */
const REST_DEEP_AT = 0.9;

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

/** Every dial, as a list — the morph loop walks this each frame. */
const TUNER_KEYS = Object.keys(FUTURE_TUNER_DEFAULTS) as (keyof FutureTuners)[];

/** Ease one pin toward another. HUE TAKES THE SHORT WAY: lerping 350° → 10°
 *  the long way sweeps the organism through the entire wheel — the "rainbow
 *  wipe" that made a small colour nudge look like a costume change. */
function easePin(a: HueSL, b: HueSL, k: number): { pin: HueSL; moved: number } {
  let dh = ((b.hue - a.hue) % 360 + 540) % 360 - 180;
  const ds = b.sat - a.sat;
  const dl = b.light - a.light;
  return {
    pin: { hue: (a.hue + dh * k + 360) % 360, sat: a.sat + ds * k, light: a.light + dl * k },
    moved: Math.abs(dh) / 180 + Math.abs(ds) + Math.abs(dl),
  };
}

/** One step of a whole ColorState toward another; `settled` when there is
 *  nothing left worth re-deriving a palette for. */
function easeColor(a: ColorState, b: ColorState, k: number): { state: ColorState; settled: boolean } {
  const p = easePin(a.primary, b.primary, k);
  const s = easePin(a.secondary, b.secondary, k);
  // a tinge that appears or disappears mid-morph has nothing to travel from, so
  // it is adopted whole — the derived complement covers the gap either way.
  const t = a.tinge && b.tinge ? easePin(a.tinge, b.tinge, k) : null;
  const dc = b.contrast - a.contrast;
  const moved = p.moved + s.moved + (t?.moved ?? 0) + Math.abs(dc);
  return {
    state: {
      primary: p.pin,
      secondary: s.pin,
      ...(t ? { tinge: t.pin } : b.tinge ? { tinge: b.tinge } : {}),
      contrast: a.contrast + dc * k,
    },
    settled: moved < 0.002,
  };
}

/** Deterministic 32-bit LCG for CPU-side seeded choices. */
function lcg(seed: number): () => number {
  let s = seed >>> 0 || 1;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

export class FutureOrganism {
  readonly canvas: HTMLCanvasElement;
  ok: boolean;
  error: string | null = null;

  private gl: WebGL2RenderingContext | null = null;
  private prog: WebGLProgram | null = null;
  private u: Record<string, WebGLUniformLocation | null> = {};
  private quad: WebGLBuffer | null = null;
  private vao: WebGLVertexArrayObject | null = null;

  private maxDpr: number;
  private W = 1;
  private H = 1;

  private running = false;
  private raf = 0;
  private contextLost = false;
  private t0 = performance.now();
  /** a still's clock (see freezeClock): when set, every frame is this instant */
  private heldT: number | null = null;
  private lastT = 0;
  private visHandler: (() => void) | null = null;
  // frame pacing — 0 = every rAF; else the minimum ms between draws (fpsCap)
  private minFrameMs = 0;
  private lastDraw = 0;
  // DYNAMIC RENDERING (rest ladder) — 0 = fully lit, 1 = frosted over by a
  // reader/modal (setCalm, fed by the window calm bus). `calm` eases toward
  // `calmTarget` in step(); it winds fps + backing resolution + the hottest
  // shader loops down TOGETHER, and everything returns exactly when the frost
  // lifts (tuner state is never touched — trims are uniform-time only).
  private calm = 0;
  private calmTarget = 0;
  private restTier = 0; // 0 lit · 1 mid (REST_MID_DPR) · 2 deep (REST_DPR)
  // reused fire-pulse uniform scratch [petalIndex, phase] ×8 — hoisted out of the
  // draw so we don't allocate a Float32Array (GC pressure) on every frame.
  private readonly pulseData = new Float32Array(16);

  // live state
  //
  // NOTHING HERE IS EVER SET DIRECTLY BY A CALLER. law_netti_never_cuts: every
  // dial, the age, and the colour are LIVE values that ease toward a TARGET in
  // step(). A caller hands netti a destination; netti travels there itself.
  //
  // This is the whole reason the overture's "becoming" reads as one creature
  // changing its mind rather than a slideshow: setTuners() used to write the
  // shader's inputs directly, so a new form appeared between two frames — a cut,
  // mid-rotation. Now the geometry, the growth and the pigment all move at the
  // speed of a living thing, from wherever they happen to be.
  private tuners: FutureTuners;
  private tunerTgt: FutureTuners;
  private palette: DerivedPalette;
  private colorState: ColorState;
  private colorTgt: ColorState;
  private colorMoving = false;
  private bg: RGB = [0.972, 0.96, 0.937];
  /** paint as if on a ground this dark (see groundLaw) — exports + setGroundOverride */
  private darkOverride: number | null = null;
  /** PRESENCE — keep the paper creature on a dark ground (see setPresence).
   *  ON BY DEFAULT since 2026-10-02: every netti everywhere keeps its cells lit
   *  on a dark ground (law_netti_presence). Pass `presence: 0` for the old law. */
  private presence = 1;
  private seed = 7;
  private age = 0.62;
  private pointer: { x: number; y: number } | null = null;
  private pointerSm = { x: 0, y: 0, in: 0 };
  // spin spring (shortest-path targets, differential applied in-shader)
  private spinCur = 0;
  private spinVel = 0;
  private spinTarget = 0;
  // firing pulses travelling the venation (up to 8 live)
  private pulses: FirePulse[] = [];
  private swellT = 0; // brief whole-body swell on burst
  // whole-organism burst wave: born-time + direction (+1 send out, -1 gather in)
  private burstT = -10;
  private burstDir = 1;
  // FOCUS VIEW — an eased engine-level camera (the dive choreography): zoom
  // toward a region of the body, ease back out on clear.
  private viewCur = { x: 0, y: 0, z: 1 };
  private viewTgt = { x: 0, y: 0, z: 1 };

  constructor(opts: FutureOrganismOptions) {
    this.canvas = opts.canvas;
    this.maxDpr = opts.maxDpr ?? 1.5;
    this.tuners = { ...FUTURE_TUNER_DEFAULTS, ...(opts.tuners ?? {}) };
    this.tunerTgt = { ...this.tuners };
    this.colorState = opts.color ?? DEFAULT_COLOR;
    this.colorTgt = this.colorState;
    this.palette = derivePalette(this.colorState);
    if (opts.bg) this.bg = opts.bg;
    if (opts.seed != null) this.seed = opts.seed;
    if (opts.age != null) this.age = clamp01(opts.age);
    if (opts.presence != null) this.presence = clamp01(opts.presence);
    if (opts.fpsCap && opts.fpsCap > 0) this.minFrameMs = 1000 / opts.fpsCap;

    const gl = this.canvas.getContext("webgl2", {
      antialias: false,
      alpha: true,
      premultipliedAlpha: true,
      // only the veil (sampling this canvas) needs the buffer kept — see option
      preserveDrawingBuffer: opts.preserveDrawingBuffer ?? true,
    }) as WebGL2RenderingContext | null;

    if (!gl) {
      this.ok = false;
      this.error = "webgl2 unavailable";
      return;
    }
    this.gl = gl;
    // CONTEXT-LOSS RECOVERY — on a memory-constrained phone the GPU can evict this
    // context mid-scroll (the "crash": netti silently vanishes onto a dead canvas).
    // preventDefault keeps the context recoverable; on restore we rebuild the
    // program + buffers and resume, so it heals instead of staying blank.
    this.canvas.addEventListener("webglcontextlost", (e) => {
      e.preventDefault();
      this.contextLost = true;
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = 0;
    }, false);
    this.canvas.addEventListener("webglcontextrestored", () => {
      if (!this.gl) return;
      try {
        this.buildProgram(this.gl);
        this.contextLost = false;
        this.resize();
        if (this.running) { this.running = false; this.start(); }
      } catch (err) {
        this.ok = false;
        this.error = err instanceof Error ? err.message : String(err);
      }
    }, false);
    try {
      this.buildProgram(gl);
      this.ok = true;
    } catch (e) {
      this.ok = false;
      this.error = e instanceof Error ? e.message : String(e);
    }
  }

  // ── public control surface ────────────────────────────────────────────────

  // Every setter below names a DESTINATION. Pass `now: true` only when there is
  // no "from" to travel out of — the first paint of a fresh mount, or a seed
  // change that makes this a different organism entirely. Anything else morphs.
  setTuner<K extends keyof FutureTuners>(key: K, v: FutureTuners[K], now = false): void {
    this.tunerTgt[key] = clamp01(v);
    if (now) this.tuners[key] = this.tunerTgt[key];
  }
  setTuners(p: Partial<FutureTuners>, now = false): void {
    for (const k of Object.keys(p) as (keyof FutureTuners)[]) {
      const v = p[k];
      if (typeof v !== "number") continue;
      this.tunerTgt[k] = clamp01(v);
      if (now) this.tuners[k] = this.tunerTgt[k];
    }
  }
  /** The dials netti is WEARING right now (mid-morph, this is not the target). */
  getTuners(): FutureTuners {
    return { ...this.tuners };
  }

  setColor(c: ColorState, now = false): void {
    this.colorTgt = c;
    this.colorMoving = true;
    if (now) {
      this.colorState = c;
      this.palette = derivePalette(c);
      this.colorMoving = false;
    }
  }

  /** REST MODE — 0 = fully lit, 1 = a reader/modal frost sits over the organism.
   *  Eased internally so the ladder engages/releases with the frost's own fade.
   *  Under rest the engine renders fewer frames at a lower backing resolution
   *  with its per-pixel hot loops wound down — visually identical behind the
   *  blur, a fraction of the GPU, and the scrim's backdrop-filter re-runs only
   *  on the frames that actually draw. */
  setCalm(v: number): void {
    this.calmTarget = clamp01(v);
  }
  setBg(bg: RGB): void {
    this.bg = bg;
  }
  setSeed(seed: number): void {
    this.seed = seed >>> 0 || 1;
  }
  /** Age is already eased (and breathed) by the mount — this takes it as given.
   *  `now` is accepted so every setter reads the same at the call site. */
  setAge(age: number, _now = false): void {
    this.age = clamp01(age);
  }

  /** Rotate toward `rad` — the spring takes the SHORTEST path (≤ π). */
  setRotation(rad: number): void {
    let d = (rad - this.spinCur) % TAU;
    if (d > Math.PI) d -= TAU;
    if (d < -Math.PI) d += TAU;
    this.spinTarget = this.spinCur + d;
  }

  /** Pointer in canvas CSS pixels; null = left. */
  setPointer(x: number | null, y?: number): void {
    this.pointer = x == null || y == null ? null : { x, y };
  }

  /** The live colour pins (the mount publishes these to the UI tint vars). */
  get color(): ColorState {
    return this.colorState;
  }

  /** FOCUS — glide+zoom the view onto a region of the body (the dive). */
  focusOn(f: { angle: number; dist: number; zoom?: number }): void {
    const r = Math.min(1, Math.max(0, f.dist)) * 0.85;
    this.viewTgt = {
      x: Math.cos(f.angle) * r,
      y: Math.sin(f.angle) * r,
      // A FOCUS WITHOUT A ZOOM ASKS THE CAMERA TO LOOK, NOT TO MAGNIFY. The old
      // default was 1.6, which is not a default so much as an instruction nobody
      // gave: every caller that means to magnify passes its own number, and all
      // of them sit between 1.08 and 1.28 (focusForDepth caps itself at 1.12 and
      // says why — "a soft spread, not a balloon"). A surface that passes only an
      // angle and a distance — the admin console does — was silently magnified
      // 60%, which puts the body's outer reach past the canvas on every side. The
      // shader fits netti at 0.47 of the frame's half-height and melts the last 3%
      // of the border; magnified 1.6x there is nothing left to melt, so the frame
      // itself becomes the edge and netti reads as a soft-cornered BOX sitting
      // exactly where the creature should be. 1.0 is the honest default.
      z: Math.max(1, f.zoom ?? 1),
    };
  }

  clearFocus(): void {
    this.viewTgt = { x: 0, y: 0, z: 1 };
  }

  /** Fire one neuron pulse along a random (or given) petal's venation. */
  fire(petal?: number): void {
    const rng = lcg((this.seed ^ (performance.now() & 0xffff)) >>> 0);
    const n = this.petalCount();
    this.pulses.push({
      petal: petal ?? Math.floor(rng() * n),
      born: this.now(),
      speed: 0.9 + rng() * 0.6,
    });
    if (this.pulses.length > 8) this.pulses.shift();
  }

  /** Burst — the WHOLE organism fires at once as one wave. Direction encodes
   *  intent: "out" (sending) sweeps the glow root→tip; "in" (receiving) sweeps
   *  tip→root. Plus a brief whole-body swell. */
  burst(dir: "out" | "in" = "out"): void {
    this.burstT = this.now();
    this.burstDir = dir === "in" ? -1 : 1;
    this.swellT = this.now();
  }

  clearPulses(): void {
    this.pulses = [];
    this.burstT = -10;
  }

  // ── lifecycle ─────────────────────────────────────────────────────────────

  start(): void {
    if (!this.ok || this.running) return;
    this.running = true;
    this.t0 = performance.now() - this.lastT * 1000;
    const loop = (now: number) => {
      if (!this.running) return;
      // fps ceiling — skip the draw (not the loop) until the frame budget has
      // passed; -0.5ms tolerance so a 33.34ms rAF beat still lands 30fps clean.
      // The budget halves (an even beat) once a frost is more than half settled
      // over the organism (frameBudgetMs).
      const budget = this.frameBudgetMs();
      if (
        !document.hidden &&
        !this.contextLost &&
        (budget === 0 || now - this.lastDraw >= budget - 0.5)
      ) {
        this.lastDraw = now;
        this.render();
      }
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
    if (!this.visHandler) {
      this.visHandler = () => {
        // re-sync the clock after a hidden gap so nothing jumps
        if (!document.hidden) this.t0 = performance.now() - this.lastT * 1000;
      };
      document.addEventListener("visibilitychange", this.visHandler);
    }
  }

  stop(): void {
    this.running = false;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    if (this.visHandler) {
      document.removeEventListener("visibilitychange", this.visHandler);
      this.visHandler = null;
    }
  }

  destroy(): void {
    this.stop();
    const gl = this.gl;
    if (gl) {
      if (this.prog) gl.deleteProgram(this.prog);
      if (this.quad) gl.deleteBuffer(this.quad);
      if (this.vao) gl.deleteVertexArray(this.vao);
    }
    this.gl = null;
  }

  /** Current min-ms-between-draws — the fps cap stretched by the rest ladder. */
  private frameBudgetMs(): number {
    // an even beat, never a blend: the lit rate, or exactly half of it
    if (this.calm < 0.5) return this.minFrameMs;
    const base = this.minFrameMs > 0 ? 1000 / this.minFrameMs : 60;
    return 1000 / Math.max(24, base / 2);
  }

  /** Size the backing store to the canvas and, when that changed it, draw at
   *  once. ⚠️ Setting canvas.width CLEARS it, and the ResizeObserver that calls
   *  this runs after layout but before paint: without a draw here, the frame
   *  that gets painted is the empty one and netti blinks out for a frame. That
   *  was the flash on scroll (a phone's url bar resizing the fixed wrapper) and
   *  on any layout shift under the creature. */
  resize(): void {
    if (this.applySize() && this.running && !document.hidden && !this.contextLost) this.drawScene(this.W, this.H, false);
  }

  /** the sizing half of resize(): true when the backing store changed. */
  private applySize(): boolean {
    if (!this.gl) return false;
    // rest: under a fully settled frost the backing store drops to REST_DPR
    // device px per css px; the frost above hides the upscale entirely. It
    // re-allocates twice per frost (in, out), never on a ladder of tiers.
    const lit = Math.min(window.devicePixelRatio || 1, this.maxDpr);
    // RESOLUTION FOLLOWS THE FROST (measured 2026-09-27: a netti drawing at
    // full resolution under a lit frost held the dashboard at ~30fps while
    // scrolling; the same page at the deep tier ran at ~79). Three coarse
    // tiers, re-allocated only when the tier changes.
    const dpr = this.restTier === 2 ? Math.min(lit, REST_DPR) : this.restTier === 1 ? Math.min(lit, REST_MID_DPR) : lit;
    const w = Math.max(1, Math.round(this.canvas.clientWidth * dpr));
    const h = Math.max(1, Math.round(this.canvas.clientHeight * dpr));
    if (w === this.W && h === this.H) return false;
    this.W = w;
    this.H = h;
    this.canvas.width = w;
    this.canvas.height = h;
    return true;
  }

  renderFrame(): void {
    if (this.ok) this.render();
  }

  /** Paint as if standing on a ground this dark (0 paper … 1 glass — see
   *  lib/organism/groundLaw), whatever `setBg` says; null returns to the
   *  engine's own ground. For a transparent export whose destination is not
   *  the ground on screen (the /media designer's "lands on"). */
  setGroundOverride(dark: number | null): void {
    this.darkOverride = dark == null ? null : clamp01(dark);
  }

  /** PRESENCE (0…1, default 1) — how much of the PAPER creature netti keeps on
   *  a dark ground. The ground law slides the body from transmitted watercolour
   *  (paper) to stained glass lit from within (glass) as the ground darkens;
   *  glass reads as a glow, and in the glow the layering of the washes, the
   *  pooled veins and the node cells all drown. At presence 1 the BODY stays
   *  paper on any ground (its washes, its depth, its alpha), while the ground
   *  still speaks: the thin washes take a luminous tint of the ground's own hue
   *  and let a little of it through, the deepest overlaps lift out of black, and
   *  the node cells and veins GLOW by the ground's darkness. The pearl keeps the
   *  real ground law. 0 is bit-for-bit the engine as it was before 2026-10-02;
   *  1 is the default on every surface (law_netti_presence). */
  setPresence(p: number): void {
    this.presence = clamp01(p);
  }

  /** A STILL'S CLOCK. Hold every frame at `seconds` since birth (null lets time
   *  run again). A still is one instant: exports taken minutes apart, for
   *  different grounds, then show the same breath, the same sway and the same
   *  motes, so a card re-baked for a new ground never shifts its pose. Holding
   *  past ~1s also keeps a fresh engine out of its birth swell (uSwell). Only
   *  stills use it; a running engine never should. */
  freezeClock(seconds: number | null): void {
    this.heldT = seconds == null || !Number.isFinite(seconds) ? null : Math.max(0, seconds);
  }

  // ── export — natively sharp at any resolution (single-pass renderer) ─────

  /** Render one frame at `longEdge` pixels (organism fit inside), read back the
   *  TRUE per-pixel alpha, auto-crop to the painted bounds, return a PNG.
   *
   *  ⚠️ The PNG wears the look of the ground the engine is set to (see
   *  lib/organism/groundLaw). To bake for somewhere else, use
   *  `exportForGrounds` with the DESTINATION's darkness. */
  exportTransparentPng(longEdge = 2048, pad = 0.04): string {
    return this.exportForGrounds([null], longEdge, pad)[0] ?? "";
  }

  /** The same frame painted for several grounds at once — one PNG per entry in
   *  `darks` (0 paper … 1 glass; null = the engine's own ground), all cropped to
   *  ONE shared box so the set stays pixel-registered and can be blended.
   *
   *  WHY IT IS NOT ONE STRAIGHT PASS. The shared box can only be known after
   *  every ground has been painted, and holding them all to find it costs
   *  `S² × 4` bytes EACH — 67 MB per ground at 4096, 335 MB for a five-stop
   *  sheet, which is where a tab dies. So:
   *    · one ground  — one render, box and crop from the same buffer.
   *    · several     — the box is PROBED at a small size first (it is geometry,
   *      so it scales), then each ground is rendered once at full size and
   *      cropped straight to a PNG. The probe is biased OUTWARD: a box a few
   *      pixels too wide only carries more transparent margin, while one a few
   *      pixels too tight would clip a faint outer mote.
   *  Either way the memory ceiling is a single readback, and the frames are
   *  identical across passes because each entry's clock is frozen. */
  exportForGrounds(darks: (number | null)[], longEdge = 2048, pad = 0.04): string[] {
    const out: string[] = [];
    const ok = this.exportEach(darks, longEdge, pad, (off) => {
      out.push(off.toDataURL("image/png"));
      off.width = off.height = 1; // a 4K frame is ~50 MB of canvas — let it go
    });
    return ok ? out : darks.map(() => "");
  }

  /** The same frames as `exportForGrounds`, handed back as 2D canvases
   *  (straight alpha, cropped) instead of PNG data URLs: no encode and no
   *  decode, for a caller that composites in the browser straight away (the
   *  share card). The caller owns the canvases (shrink them to 1×1 to free
   *  them). null entries when the export could not be made. */
  exportCanvases(darks: (number | null)[], longEdge = 2048, pad = 0.04): (HTMLCanvasElement | null)[] {
    const out: HTMLCanvasElement[] = [];
    const ok = this.exportEach(darks, longEdge, pad, (off) => out.push(off));
    return ok ? out : darks.map(() => null);
  }

  /** the export body: renders, crops and hands each ground's frame to `take`
   *  as it is made (one at a time, so the memory ceiling stays one readback).
   *  false when nothing could be exported. */
  private exportEach(
    darks: (number | null)[],
    longEdge: number,
    pad: number,
    take: (off: HTMLCanvasElement) => void,
  ): boolean {
    const gl = this.gl;
    if (!gl || !this.prog || !darks.length) return false;
    const t0 = this.t0;
    const prevOverride = this.darkOverride;
    const frozen = performance.now();
    const S = Math.max(256, Math.min(8192, Math.round(longEdge)));
    // freeze the clock per entry, so every ground is the same instant — and the
    // same instant again if it is painted a second time
    const clock = darks.map(() => t0 + (performance.now() - frozen));

    const target = (size: number) => {
      const tex = gl.createTexture()!;
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, size, size, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      const fbo = gl.createFramebuffer()!;
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
      return {
        shoot: (i: number, into: Uint8Array) => {
          gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
          this.darkOverride = darks[i] == null ? prevOverride : clamp01(darks[i]!);
          this.t0 = clock[i];
          this.drawScene(size, size, true);
          gl.readPixels(0, 0, size, size, gl.RGBA, gl.UNSIGNED_BYTE, into);
        },
        dispose: () => {
          gl.bindFramebuffer(gl.FRAMEBUFFER, null);
          gl.deleteFramebuffer(fbo);
          gl.deleteTexture(tex);
        },
      };
    };

    /** widen `box` to cover every painted pixel in `px`. Rows already inside the
     *  box can only widen it SIDEWAYS, so their middle is skipped — that is most
     *  of the image, and it is what keeps a 4K scan quick. */
    const grow = (px: Uint8Array, size: number, box: number[]) => {
      for (let y = 0; y < size; y++) {
        const row = y * size * 4;
        if (box[3] >= 0 && y >= box[1] && y <= box[3]) {
          for (let x = 0; x < box[0]; x++) if (px[row + x * 4 + 3] > 3) { box[0] = x; break; }
          for (let x = size - 1; x > box[2]; x--) if (px[row + x * 4 + 3] > 3) { box[2] = x; break; }
          continue;
        }
        for (let x = 0; x < size; x++) {
          if (px[row + x * 4 + 3] > 3) {
            if (x < box[0]) box[0] = x;
            if (x > box[2]) box[2] = x;
            if (y < box[1]) box[1] = y;
            if (y > box[3]) box[3] = y;
          }
        }
      }
    };

    let full: { shoot: (i: number, into: Uint8Array) => void; dispose: () => void } | null = null;
    let px: Uint8Array;
    try {
      px = new Uint8Array(S * S * 4);
    } catch {
      return false; // no room for a frame this big
    }

    // [minX, minY, maxX, maxY] at full size
    let box = [S, S, -1, -1];
    try {
      if (darks.length > 1) {
        // ── probe — the shared box, cheaply
        const P = Math.min(S, 1024);
        const probe = target(P);
        const small = new Uint8Array(P * P * 4);
        const pbox = [P, P, -1, -1];
        try {
          for (let i = 0; i < darks.length; i++) {
            probe.shoot(i, small);
            grow(small, P, pbox);
          }
        } finally {
          probe.dispose();
        }
        if (pbox[2] < 0) return false;
        const k = S / P;
        const slack = Math.ceil(k) + Math.round(S * 0.01); // bias outward
        box = [
          Math.max(0, Math.floor(pbox[0] * k) - slack),
          Math.max(0, Math.floor(pbox[1] * k) - slack),
          Math.min(S - 1, Math.ceil(pbox[2] * k) + slack),
          Math.min(S - 1, Math.ceil(pbox[3] * k) + slack),
        ];
      }

      full = target(S);
      for (let i = 0; i < darks.length; i++) {
        full.shoot(i, px);
        if (darks.length === 1) {
          grow(px, S, box);
          if (box[2] < 0) return false;
          const padPx = Math.round(Math.max(box[2] - box[0], box[3] - box[1]) * pad);
          box = [
            Math.max(0, box[0] - padPx),
            Math.max(0, box[1] - padPx),
            Math.min(S - 1, box[2] + padPx),
            Math.min(S - 1, box[3] + padPx),
          ];
        } else if (i === 0) {
          const padPx = Math.round(Math.max(box[2] - box[0], box[3] - box[1]) * pad);
          box = [
            Math.max(0, box[0] - padPx),
            Math.max(0, box[1] - padPx),
            Math.min(S - 1, box[2] + padPx),
            Math.min(S - 1, box[3] + padPx),
          ];
        }
        const [minX, minY, maxX, maxY] = box;
        const cw = maxX - minX + 1;
        const ch = maxY - minY + 1;
        const off = document.createElement("canvas");
        off.width = cw;
        off.height = ch;
        const ctx = off.getContext("2d")!;
        const img = ctx.createImageData(cw, ch);
        for (let y = 0; y < ch; y++) {
          const sy = S - 1 - (minY + y);
          const srcRow = (sy * S + minX) * 4;
          const dstRow = y * cw * 4;
          // un-premultiply to straight alpha for the PNG
          for (let x = 0; x < cw; x++) {
            const si = srcRow + x * 4;
            const di = dstRow + x * 4;
            const a = px[si + 3];
            if (a > 0) {
              const inv = 255 / a;
              img.data[di] = Math.min(255, px[si] * inv);
              img.data[di + 1] = Math.min(255, px[si + 1] * inv);
              img.data[di + 2] = Math.min(255, px[si + 2] * inv);
            }
            img.data[di + 3] = a;
          }
        }
        ctx.putImageData(img, 0, 0);
        take(off);
      }
    } finally {
      this.darkOverride = prevOverride;
      this.t0 = t0;
      full?.dispose();
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    }
    return true;
  }

  // ── internals ─────────────────────────────────────────────────────────────

  private now(): number {
    if (this.heldT != null) return this.heldT;
    return (performance.now() - this.t0) / 1000;
  }

  private petalCount(): number {
    // CAPACITY only (abundance). Age no longer gates the count — every petal
    // has its own birth threshold in the shader and grows in individually, so
    // memory growth adds petals outward along the spiral with no popping.
    //
    // Read off the TARGET, not the eased value: the count is a whole number, and
    // the shader divides it into per-layer whorls (`mL`). Sweeping it through a
    // morph would re-space every whorl a hundred times on the way — a shimmer far
    // worse than the one snap this avoids. Capacity is the one dial that arrives
    // all at once; everything about the petals' SHAPE still travels.
    return 36 + Math.round(this.tunerTgt.abundance * 160);
  }

  private render(): void {
    // NB: resize() is NOT called per-frame. It reads canvas.clientWidth/Height,
    // which forces a synchronous layout flush every frame — and during a section
    // relocation (the shell commits new inline styles on the backdrop) that turns
    // into a per-frame reflow storm on the main thread (the "stuck when scrolling
    // between spaces" jank). The ResizeObserver on the wrapper (MemoryOrganism)
    // already drives resize() on every real layout change, so this is pure waste.
    this.step();
    // calm re-tiers the backing store in coarse steps (0..3). resize() reads
    // clientWidth (a layout flush), so it runs only on a tier CHANGE (≤3 per
    // frost transition, never per frame) and draws in the same frame — the
    // buffer is never presented blank between the realloc and the draw.
    // deep rest only under a frost that has fully settled AND is staying; the
    // moment the target lets go (the sheet closing, the reader scrolling back
    // up) the full-size store returns in this same frame, before the frost has
    // thinned at all. Two re-allocations per frost, never a ladder of them.
    const tier =
      this.calmTarget >= REST_DEEP_AT && this.calm >= REST_DEEP_AT - 0.05
        ? 2
        : this.calmTarget >= REST_MID_AT && this.calm >= REST_MID_AT - 0.05
          ? 1
          : 0;
    if (tier !== this.restTier) {
      this.restTier = tier;
      this.applySize(); // drawn just below, in this same frame
    }
    this.drawScene(this.W, this.H, false);
  }

  /** Advance springs/pulses — dt-scaled, frame-rate independent. */
  private step(): void {
    const t = this.now();
    const dt = Math.min(0.05, Math.max(0.0001, t - this.lastT));
    this.lastT = t;
    // calm eases toward its target so the rest ladder engages/releases with the
    // frost's own fade rather than snapping fps/resolution mid-scroll.
    this.calm += (this.calmTarget - this.calm) * (1 - Math.exp(-dt / 0.3));
    if (Math.abs(this.calmTarget - this.calm) < 0.005) this.calm = this.calmTarget;

    // ── THE MORPH — geometry and pigment travel, they never jump ─────────────
    // Shape is a decision, colour is a mood, so shape moves a little faster.
    //
    // AGE IS NOT EASED HERE, deliberately: the mount already eases it (its own
    // 0.14s ramp) and then rides a breath oscillation on top. A second lag over
    // that would damp the breath into a flat line — netti would stop breathing
    // to fix a problem it never had.
    const kT = 1 - Math.exp(-dt / 0.62);
    for (const k of TUNER_KEYS) {
      const d = this.tunerTgt[k] - this.tuners[k];
      this.tuners[k] = Math.abs(d) < 0.0004 ? this.tunerTgt[k] : this.tuners[k] + d * kT;
    }
    if (this.colorMoving) {
      const next = easeColor(this.colorState, this.colorTgt, 1 - Math.exp(-dt / 0.5));
      this.colorState = next.state;
      this.palette = derivePalette(next.state);
      this.colorMoving = !next.settled;
    }
    // continuous revolve — the target itself rotates, endlessly past 360°; the
    // spring chases it, so the body carries real momentum (outer petals trail).
    this.spinTarget += this.tuners.revolve * 0.35 * dt;
    // spin spring (critically-ish damped)
    const k = 14;
    const c = 2 * 0.9 * Math.sqrt(k);
    const a = k * (this.spinTarget - this.spinCur) - c * this.spinVel;
    this.spinVel += a * dt;
    this.spinCur += this.spinVel * dt;
    // KEEP THE SPIN SMALL. spinCur grows without bound (the ambient drift, the
    // revolve tuner, every dive) and it is uploaded as a float32 uniform: past a
    // few thousand radians the mantissa can no longer resolve one petal's slot
    // from the next, the whorls smear into stair-steps, and after days the whole
    // body collapses into one smooth swollen disc (the "empty white ball" a
    // long-open dashboard tab shows). Fold BOTH spring ends by the same multiple
    // so the spring's own state (target − current, the velocity) is untouched.
    // The period is 4π, not 2π: the motes read `uSpin * 0.5`, and 4π is the
    // smallest fold that keeps that term continuous too. Nothing downstream can
    // tell — every use of uSpin is periodic in it.
    if (Math.abs(this.spinCur) > SPIN_WRAP) {
      const fold = Math.floor(this.spinCur / SPIN_WRAP) * SPIN_WRAP;
      this.spinCur -= fold;
      this.spinTarget -= fold;
    }
    // focus view eases toward its target (the dive glide)
    const kV = 1 - Math.exp(-dt / 0.22);
    this.viewCur.x += (this.viewTgt.x - this.viewCur.x) * kV;
    this.viewCur.y += (this.viewTgt.y - this.viewCur.y) * kV;
    this.viewCur.z += (this.viewTgt.z - this.viewCur.z) * kV;
    // pointer smoothing (canvas CSS px → NDC-ish, done in draw)
    const pin = this.pointer ? 1 : 0;
    this.pointerSm.in += (pin - this.pointerSm.in) * (1 - Math.exp(-dt / 0.18));
    if (this.pointer) {
      const px = this.pointer.x / Math.max(1, this.canvas.clientWidth);
      const py = this.pointer.y / Math.max(1, this.canvas.clientHeight);
      this.pointerSm.x += (px - this.pointerSm.x) * (1 - Math.exp(-dt / 0.12));
      this.pointerSm.y += (py - this.pointerSm.y) * (1 - Math.exp(-dt / 0.12));
    }
    // drop dead pulses (travel ~1.6s)
    this.pulses = this.pulses.filter((p) => t - p.born < 1.8);
  }

  private drawScene(w: number, h: number, forExport: boolean): void {
    const gl = this.gl!;
    const t = this.now();
    gl.viewport(0, 0, w, h);
    gl.disable(gl.BLEND);
    gl.disable(gl.DEPTH_TEST);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.useProgram(this.prog);
    gl.bindVertexArray(this.vao);

    const T = this.tuners;
    const P = this.palette;
    const u = this.u;
    const set1 = (n: string, v: number) => u[n] && gl.uniform1f(u[n], v);
    const set2 = (n: string, a: number, b: number) => u[n] && gl.uniform2f(u[n], a, b);
    const set3 = (n: string, c: RGB) => u[n] && gl.uniform3f(u[n], c[0], c[1], c[2]);

    set2("uRes", w, h);
    // KEEP TIME SMALL. `t` is seconds since mount and it also rides to the GPU
    // as a float32: at a day's worth of seconds the mantissa steps by ~8ms, the
    // mote twinkle strobes and the venation wave smears. Fold it by TIME_WRAP —
    // chosen as an exact common period of the shader's two fixed clocks (the
    // breath at 0.5 rad/s, the scintillation at 2.2 rad/s), so those never
    // jump; only the per-mote phases re-seat, one frame every ~105 minutes.
    // Pulses/bursts/swell keep the unwrapped clock (they measure elapsed time).
    set1("uTime", t % TIME_WRAP);
    set1("uSeed", (this.seed % 1024) + 1);
    set1("uAge", this.age);
    set1("uCount", this.petalCount());
    set1("uSpin", this.spinCur);
    // clamped spring velocity — drives the per-layer inertia lag in-shader
    set1("uSpinVel", Math.max(-3, Math.min(3, this.spinVel)));
    set1("uDark", this.darkOverride ?? groundDarkness(this.bg));
    set1("uPresence", this.presence);
    set3("uBg", this.bg);
    set3("uCore", P.core);
    set3("uBody", P.body);
    set3("uOuter", P.outer);
    set3("uFringe", P.fringe);
    set3("uHalo", P.halo);
    set3("uGlow", P.glow);
    // tuners
    set1("uFormation", T.formation);
    set1("uRadialBias", T.radialBias);
    set1("uPetalLen", T.petalLength);
    set1("uPetalWid", T.petalWidth);
    set1("uTip", T.tipSharpness);
    set1("uBend", T.bend);
    set1("uVariety", T.variety);
    // REST TRIMS — under frost the hottest per-pixel work winds down. Uniform-
    // time only (tuner state untouched) so the full look returns exactly when
    // the frost lifts; exports always render fully lit. uMotes reaching 0 lets
    // the shader skip its 40-mote loop outright (it's gated on uMotes > 0.02).
    const rest = forExport ? 0 : this.calm;
    set1("uVenation", T.venation * (1 - rest * 0.5));
    set1("uNodes", T.nodes * (1 - rest * 0.5));
    set1("uGlass", T.glass);
    set1("uInnerLight", T.innerLight);
    set1("uSway", T.sway);
    set1("uTempo", T.tempo);
    set1("uBreath", T.breath);
    set1("uMotes", T.motes * Math.max(0, 1 - rest * 1.2));
    set1("uMoteSize", T.moteSize);
    set1("uTouch", T.touch);
    set1("uCellSize", T.cellSize);
    set1("uCellGlow", T.cellGlow);
    set1("uCellLight", T.cellLight);
    set1("uCellWobble", T.cellWobble);
    set1("uHueSpread", T.hueSpread);
    // pointer (normalized 0..1, +swell)
    set2("uPointer", this.pointerSm.x, this.pointerSm.y);
    set1("uPointerIn", this.pointerSm.in * (forExport ? 0 : 1));
    set1("uSwell", Math.max(0, 1 - (t - this.swellT) / 0.9));
    // whole-body burst wave: 0..~1.4 progress along the petals; <0 = inactive
    const burstAge = t - this.burstT;
    set1("uBurst", burstAge >= 0 && burstAge < 1.5 ? burstAge / 1.2 : -1);
    set1("uBurstDir", this.burstDir);
    // focus view (eased in step)
    set2("uViewC", this.viewCur.x, this.viewCur.y);
    set1("uViewZ", this.viewCur.z);
    // fire pulses → packed uniform array [petalIndex, phase] ×8 (reused scratch)
    const pulseData = this.pulseData;
    for (let i = 0; i < 8; i++) {
      const p = this.pulses[i];
      if (p) {
        pulseData[i * 2] = p.petal;
        pulseData[i * 2 + 1] = (t - p.born) * p.speed; // 0..~1.8 along the petal
      } else {
        pulseData[i * 2] = -1;
        pulseData[i * 2 + 1] = 0;
      }
    }
    if (u["uPulses[0]"]) gl.uniform2fv(u["uPulses[0]"], pulseData);

    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    gl.bindVertexArray(null);
  }

  private buildProgram(gl: WebGL2RenderingContext): void {
    const vs = this.compile(gl, gl.VERTEX_SHADER, VERT);
    const fs = this.compile(gl, gl.FRAGMENT_SHADER, FRAG);
    const p = gl.createProgram()!;
    gl.attachShader(p, vs);
    gl.attachShader(p, fs);
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
      throw new Error(`link failed: ${gl.getProgramInfoLog(p)}`);
    }
    this.prog = p;
    gl.useProgram(p);
    const names = [
      "uRes", "uTime", "uSeed", "uAge", "uCount", "uSpin", "uSpinVel", "uDark", "uPresence", "uBg",
      "uCore", "uBody", "uOuter", "uFringe", "uHalo", "uGlow",
      "uFormation", "uRadialBias", "uPetalLen", "uPetalWid", "uTip", "uBend",
      "uVariety", "uVenation", "uNodes", "uGlass", "uInnerLight",
      "uSway", "uTempo", "uBreath", "uMotes", "uMoteSize", "uTouch",
      "uCellSize", "uCellWobble", "uHueSpread", "uCellGlow", "uCellLight",
      "uPointer", "uPointerIn", "uSwell", "uBurst", "uBurstDir", "uViewC", "uViewZ", "uPulses[0]",
    ];
    for (const n of names) this.u[n] = gl.getUniformLocation(p, n);

    this.quad = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quad);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray(this.vao);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);
  }

  private compile(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
    const sh = gl.createShader(type)!;
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
      const log = gl.getShaderInfoLog(sh);
      gl.deleteShader(sh);
      throw new Error(`compile failed: ${log}`);
    }
    return sh;
  }
}

// ── shaders ──────────────────────────────────────────────────────────────────

const VERT = `#version 300 es
layout(location=0) in vec2 aPos;
out vec2 vUv;
void main(){ vUv = aPos*0.5+0.5; gl_Position = vec4(aPos,0.0,1.0); }`;

// The fragment shader body is assembled below (FRAG). It is the heart of the
// organism — see the architecture notes at the top of this file.
const FRAG = `#version 300 es
precision highp float;
in vec2 vUv; out vec4 frag;
// ── uniforms (see drawScene) ─────────────────────────────────────────────────
uniform vec2 uRes; uniform float uTime, uSeed, uAge, uCount, uSpin, uSpinVel, uDark, uPresence;
uniform vec3 uBg, uCore, uBody, uOuter, uFringe, uHalo, uGlow;
uniform float uFormation, uRadialBias, uPetalLen, uPetalWid, uTip, uBend;
uniform float uVariety, uVenation, uNodes, uGlass, uInnerLight;
uniform float uSway, uTempo, uBreath, uMotes, uMoteSize, uTouch;
uniform float uCellSize, uCellWobble, uHueSpread, uCellGlow, uCellLight;
uniform vec2 uPointer; uniform float uPointerIn, uSwell, uBurst, uBurstDir;
uniform vec2 uViewC; uniform float uViewZ;
uniform vec2 uPulses[8];

// ── helpers ──────────────────────────────────────────────────────────────────
const float TAU = 6.28318530718;
const float GOLDEN = 2.39996322973;

float hash11(float n){ return fract(sin(n*127.1 + uSeed*311.7)*43758.5453123); }
float hash21(vec2 p){ return fract(sin(dot(p, vec2(127.1,311.7)) + uSeed)*43758.5453123); }
float vnoise(vec2 p){
  vec2 i = floor(p), f = fract(p);
  vec2 u2 = f*f*(3.0-2.0*f);
  float a = hash21(i), b = hash21(i+vec2(1,0)), c = hash21(i+vec2(0,1)), d = hash21(i+vec2(1,1));
  return mix(mix(a,b,u2.x), mix(c,d,u2.x), u2.y);
}

// hue-preserving soft knee — nothing ever blooms to flat white.
vec3 knee(vec3 c){
  float peak = max(c.r, max(c.g, c.b));
  return c / (1.0 + max(0.0, peak - 0.82) * 1.7);
}

// palette ramp — the six minted stops fanned across the organism.
// t 0 → core/glow heart · mid → body/outer · 1 → fringe/halo periphery.
vec3 paletteRamp(float t){
  t = clamp(t, 0.0, 1.0);
  if (t < 0.25) return mix(uGlow,  uBody,  smoothstep(0.0, 1.0, t/0.25));
  if (t < 0.55) return mix(uBody,  uOuter, smoothstep(0.0, 1.0, (t-0.25)/0.30));
  if (t < 0.82) return mix(uOuter, uFringe,smoothstep(0.0, 1.0, (t-0.55)/0.27));
  return mix(uFringe, uHalo, smoothstep(0.0, 1.0, (t-0.82)/0.18));
}

// ── the brand cell, as a polar radius law ────────────────────────────────────
// Generated from the CANONICAL Figma vector (lib/brand/cell.ts) — the very same
// silhouette the CSS clip-path wears on the avatar. The old superellipse (k=3)
// read visibly SQUARER than the mark at the diagonals; this is the real curve,
// so netti and the avatar are now the same shape everywhere.
${CELL_GLSL}
// ── THE CELL'S SIZE LAW — one formula, used by the drawn cell, the petal root
// rim, and the interior gradients. It used to be written out three times as
// 0.17 + uCellSize * 0.22, which (a) let them drift apart and (b) was
// dominated by its own constant: the whole minted band moved the cell by barely
// 20%, and every netti wore a heart nearly half its body wide.
//
// Two changes. The cell is CONSIDERABLY smaller at every dial position — a
// bloom's heart, not a plate the petals hide behind. And it GROWS WITH MEMORY,
// but only just: 0.88× at birth → 1.10× fully grown, a total range of 1.25×.
// Petals keep arriving outward for the whole life of the organism; the cell is
// the one part that stays nearly the size you first met.
//
// (Everything renders inside a normalized extent, so shrinking this does not
// shrink netti — it changes the ratio of heart to bloom, which is the point.)
float cellBase(){
  float grown = 1.0 - pow(1.0 - uAge, 1.65);         // the memory growth curve
  return (0.105 + uCellSize * 0.14) * (0.88 + 0.22 * grown);
}
// ── the cell rim — the BRAND cell, erect. law_netti_cell: this holds the
// innernet CELL silhouette and is NEVER morphed — the organic wobble is pinned
// OFF so the mark reads identically every frame. It still BREATHES (a uniform
// pulse — a size, not a shape change), which is life, not morph. Petals wrap
// around this shape.
float cellRadiusAt(float th, float t){
  float breathe = 1.0 + 0.03*uBreath*sin(t*0.5) + 0.05*uSwell;
  return cellBase() * 1.06 * brandCellR(th) * breathe; // a hair over the root rim, so roots stay hidden
}

// mini brand cell distance (local frame, long axis along x) — node cells and tip
// seeds are tiny versions of the mark, drawn from the SAME curve as the central
// cell. =1 at the boundary.
float pillNode(vec2 d, float s){
  float r = length(d);
  if (r < 1e-6) return 0.0;
  return r / (s * brandCellR(atan(d.y, d.x)));
}

// ── one petal's full contribution ────────────────────────────────────────────
// Layered-whorl petal: global identity i, slot angle thI, layer base radius +
// blade length + layer position ln (0 inner → 1 outer/back).
struct Petal {
  float cov;      // membrane coverage 0..1
  float rim;      // glass edge light
  float vein;     // venation (pigment depth on light grounds, light on dark)
  float node;     // node-cell light
  float hueT;     // palette position
  float depth;    // stacking depth for pigment weighting
};

Petal evalPetal(float i, float thI, float baseR, float len0, float ln, float grow, float spinAll, vec2 q, float t){
  Petal P;
  P.cov = 0.0; P.rim = 0.0; P.vein = 0.0; P.node = 0.0; P.hueT = 0.5; P.depth = 0.0;

  // per-petal DNA — everything a petal is comes from hash(i)
  float h1 = hash11(i*1.618 + 0.7);
  float h2 = hash11(i*2.113 + 4.2);
  float h3 = hash11(i*3.719 + 9.1);
  float h4 = hash11(i*5.417 + 2.6);

  // GROWTH — each petal grows in from its birth (memory law): young petals are
  // small and faint, unfurling to full size as the organism's memory passes them.
  float len = len0 * (1.0 + (h1 - 0.5) * 0.5 * uVariety) * (0.28 + 0.72 * grow);
  float wid = len * (0.18 + uPetalWid * 0.30) * (1.0 + (h2 - 0.5) * 0.6 * uVariety)
            * (0.55 + 0.45 * grow);

  // slight tangential lean; spiral formations sweep harder
  float lean = (h3 - 0.5) * 0.4 * uVariety + mix(0.34, 0.05, uFormation) * (0.5 + 0.5*ln);
  float axisAng = thI + lean;

  // local coords: u along the blade (0 root → len tip), v across
  vec2 B = vec2(cos(thI), sin(thI)) * baseR;
  vec2 d = q - B;
  float ca = cos(axisAng), sa = sin(axisAng);
  vec2 loc = vec2(d.x*ca + d.y*sa, -d.x*sa + d.y*ca);

  float un = loc.x / max(len, 1e-4);
  if (un < -0.22 || un > 1.18) return P;           // cheap reject along the axis

  // UNDULATION — the worm swim: tips travel, roots stay pinned to the rim.
  float wavePh = t * (0.8 + uTempo*2.2) - un * (2.0 + uTempo*3.0) + i * 0.7;
  loc.y -= sin(wavePh) * uSway * len * 0.13 * pow(max(un, 0.0), 1.7);
  // BEND — a sickle curve, signed per petal
  loc.y -= uBend * (h4 - 0.5) * 0.9 * len * un*un;

  float v = loc.y;
  float aun = clamp(un, 0.0, 1.0);

  // LEAF profile — broad rounded base, widest near the base third, tapering to
  // a soft rounded tip. uTip ONLY sharpens the point (0 round ovate → 1 lance);
  // it never balloons the shoulder. Normalized so the widest point == wid at any
  // tip setting (peak of aun^a·(1-aun)^b at u*=a/(a+b)).
  // The ROOT is one continuous curve: aun is remapped so the profile starts at
  // a small non-zero width and swells smoothly — no constant-width shoulder,
  // no rectangular stub, uniform and tight at the origin.
  float aexp = 0.62;                                 // rounder base (was 0.5)
  float bexp = 0.72 + uTip * 1.25;                   // gentler default taper
  float us = aexp / (aexp + bexp);
  float peak = pow(us, aexp) * pow(1.0 - us, bexp);
  float aunP = mix(0.06, 1.0, aun);                  // smooth non-zero root
  float hw = wid * (pow(aunP, aexp) * pow(1.0 - aunP + 1e-4, bexp)) / max(peak, 1e-3);
  hw *= 0.30 + 0.70 * smoothstep(0.0, 0.42, aun);    // gradual pinch — tight origin

  float dm = abs(v) - hw;                          // membrane signed distance
  // edges soften with distance from the origin — a depth-of-field feel that
  // melts the outermost tips into the ground.
  float aa = (0.005 + 0.008 * (1.0 - uGlass)) * (1.0 + smoothstep(0.72, 1.35, length(q)) * 1.6);
  float cov = 1.0 - smoothstep(-aa, aa*2.0, dm);
  // fade coverage as the blade thins below the AA width — the leaf tapers to a
  // TRUE point (no flat cap, no fuzzy bar) regardless of DOF softening, and
  // past the tip (hw→0) coverage is zero by construction.
  cov *= smoothstep(0.0, aa * 2.2, hw);
  cov *= 0.25 + 0.75 * grow;                       // newborn petals are translucent

  // membrane body — glassy: thinner in the middle, denser toward edges (x-ray).
  // The hollow FILLS toward the tip so the point stays a single clean lobe
  // (an un-filled tip reads as a bilobed butterfly where the blade is narrow).
  float vn = abs(v) / max(hw, 1e-4);
  float glassAt = uGlass * (1.0 - smoothstep(0.55, 1.0, aun) * 0.85);
  float body = mix(1.0, 0.58 + 0.42*vn*vn, glassAt);         // hollow glass heart
  body *= 0.68 + 0.32 * smoothstep(1.0, 0.2, aun);            // denser at root

  P.cov = cov * body;
  // light emerges past the shoulder — roots are tucked under the cell and must
  // NOT stack glow there (all whorls share that space).
  float rootFade = smoothstep(0.03, 0.30, aun);
  P.rim = exp(-abs(dm) * (110.0 - uGlass*50.0)) * (0.35 + 0.65*vn) * cov * rootFade;

  // WING VENATION — rays fan from the root to the blade edge (constant-vn
  // curves converge at root + tip like a feather), faint cross arcs, node
  // cells at ray×arc meetings. Vein DENSITY scales with blade size — small
  // petals keep a simple midrib, big blades earn the full network.
  // AGE LAW: a young organism shows only a whisper of venation; the full lace
  // is EARNED — months of memory (age deep in its slow tail) bring the dial
  // past ~0.3–0.4. The network is the visible record of a long life.
  float venAge = uVenation * (0.18 + 0.82 * smoothstep(0.45, 0.92, 1.0 - pow(1.0 - uAge, 1.65)));
  float sizeK = smoothstep(0.18, 0.55, len);
  if (venAge > 0.02 && cov > 0.02) {
    float vn1 = v / max(hw, 1e-4);                            // signed -1..1
    // WAVY rays — each vein meanders a little (holistic, never a ruled grid)
    float vnW = vn1 + 0.16 * (1.0 - aun) * sin(aun * (2.5 + h3 * 4.0) + h1 * 6.28);
    float fanN = 1.0 + (1.5 + venAge*3.0 + h2*1.5) * sizeK;
    float dRay = abs(fract(vnW*fanN + 0.5) - 0.5) / fanN * max(hw, 1e-4);
    float taper = smoothstep(1.08, 0.80, aun) * smoothstep(1.02, 0.90, vn) * rootFade;
    float rays = exp(-dRay * 420.0) * taper;
    float arcN = (1.5 + venAge*3.0) * sizeK;
    float arcs = 0.0;
    float meet = 0.0;
    if (arcN > 0.75) {
      float dArc = abs(fract(aun*arcN + h3) - 0.5) / arcN * len;
      arcs = exp(-dArc * 340.0) * taper;
      // node cells scattered along the veins — a tiny brand pill (our cell
      // shape) at some ray×arc meetings. Per-ray hash gating + radial jitter
      // break the concentric-ring regularity into an organic scatter.
      float rayI = floor(vnW * fanN + 0.5);
      float arcI = floor(aun * arcN + h3);
      float hn = hash11(rayI * 5.1 + arcI * 2.7 + i * 0.9);
      // keep nodes in the mid-blade — away from the narrow tip (where they'd
      // collapse together into a butterfly) and off the fat root.
      float nodeZone = smoothstep(0.12, 0.30, aun) * smoothstep(0.86, 0.62, aun)
                     * smoothstep(0.03, 0.06, hw);
      if (hn > 0.5 && nodeZone > 0.01) {
        float vnSnap = rayI / fanN;
        float aunSnap = (arcI + 0.5 - h3 + (hn - 0.75) * 0.6) / arcN;   // jitter
        vec2 nd = vec2((aun - aunSnap) * len, (vnW - vnSnap) * max(hw, 1e-4));
        // orient the cell ERECT + LANDSCAPE in SCREEN space (like the central
        // cell) — always up, never tilted with the petal. Rotate the local
        // offset local→qs→screen (petal axis + spin, minus the momentum trail).
        float worldAng = spinAll + axisAng
                       - uSpinVel * 0.22 * smoothstep(0.1, 1.2, length(q));
        float cwa = cos(worldAng), swa = sin(worldAng);
        vec2 sOff = vec2(nd.x * cwa - nd.y * swa, nd.x * swa + nd.y * cwa);
        float dSe = pillNode(sOff, 0.014);
        meet = (1.0 - smoothstep(0.74, 1.05, dSe)) * taper * nodeZone;
      }
    }
    P.vein = (rays * 0.85 + arcs * 0.07) * cov * venAge;
    if (uNodes > 0.02) {
      // nodes only live on SOLID membrane — never on the faint fringe. They
      // ride the same age law: memory cells accumulate with a long life.
      P.node = meet * 1.15 * uNodes * cov * smoothstep(0.22, 0.5, cov)
             * (0.35 + 0.65 * smoothstep(0.45, 0.92, 1.0 - pow(1.0 - uAge, 1.65)));
    }
  }
  // a single soft round dewdrop set inside the tip (dandelion) — a gentle glow,
  // NOT a hard cap, so the blade tapers to a clean point above it.
  if (uNodes > 0.02) {
    float tipD = length(vec2((un - 0.80) * len, v));
    P.node += exp(-tipD * tipD * 2200.0) * uNodes * 0.38 * sizeK * (0.3 + 0.7 * grow);
  }
  if (cov < 0.003 && P.node < 0.01) return P;

  // FIRING — pulses travel root → tip; the venation flashes as the wave passes.
  for (int k = 0; k < 8; k++) {
    if (uPulses[k].x < -0.5) continue;
    if (abs(uPulses[k].x - i) < 0.5) {
      float ph = uPulses[k].y;                      // 0.. ~1.8 along the petal
      float wave = exp(-abs(aun - ph) * 8.0) * exp(-vn*vn*2.5);
      P.vein += wave * (1.8 + P.vein * 3.0) * cov;
      P.rim  += wave * 0.7 * cov;
    }
  }

  // BURST — EVERY petal fires as one. Direction encodes intent: out (send)
  // sweeps the pulse root→tip; in (receive) sweeps tip→root, gathering to the
  // heart. Wider, brighter wave than a single fire; nodes light up as it passes.
  if (uBurst >= 0.0) {
    float ph = uBurstDir > 0.0 ? uBurst : (1.0 - uBurst);
    float wave = exp(-abs(aun - ph) * 6.0) * exp(-vn*vn*1.8) * cov;
    float env = sin(clamp(uBurst, 0.0, 1.0) * 3.14159);   // fade in+out over the sweep
    P.vein += wave * (2.2 + P.vein * 3.0) * env;
    P.node += wave * (0.6 + P.node * 6.0) * env;
    P.rim  += wave * 0.9 * env;
  }

  // hue: LAYER-COHERENT bands (like concentric petri rings) with only a small
  // per-petal wobble — big per-petal jitter averages into mud when petals stack.
  P.hueT = clamp(ln * (0.45 + 0.40*uHueSpread) + (h1 - 0.5) * 0.18 * uHueSpread + 0.20, 0.0, 1.0);
  P.depth = 1.0 - ln * 0.55 + h2 * 0.08;           // inner young layers sit on top
  return P;
}

// ── main ─────────────────────────────────────────────────────────────────────
void main(){
  float t = uTime;
  float aspect = uRes.x / uRes.y;
  vec2 p = (vUv - 0.5) * vec2(aspect, 1.0);

  // NEVER-CLIP — analytic extent: outermost whorl root + longest blade + glow pad.
  // The root rim IS the cell's law (they must move together — a rim wider than
  // the cell leaves every petal's blunt base out in the open).
  float rimA = cellBase();
  float petalMax = (0.34 + uPetalLen * 0.62) * 1.30;
  float extent = rimA * (0.80 + mix(0.4, 1.6, uRadialBias)) + petalMax + 0.10;
  float worldScale = 0.47 / extent;
  vec2 q = p / worldScale;
  // FOCUS VIEW — the dive: zoom the frame toward a region of the body
  q = q / uViewZ + uViewC * extent;

  // pointer in organism space (through the same view)
  vec2 ptr = ((uPointer - 0.5) * vec2(aspect, 1.0)) / worldScale;
  ptr.y = -ptr.y;
  ptr = ptr / uViewZ + uViewC * extent;
  // MOUSE GRAVITY — the whole body leans a HAIR toward the cursor (on top of the
  // touch lift below), so netti feels a little alive to the pointer. Deliberately
  // tiny: ~3% of the body, eased in by presence, ramped from centre (no jitter at
  // rest) and fading for a far-off cursor so it never drifts across the view.
  float ptrD = length(ptr);
  vec2  ptrDir = ptrD > 1e-3 ? ptr / ptrD : vec2(0.0);
  vec2  lean = ptrDir * (0.03 * uPointerIn * smoothstep(0.0, 0.12, ptrD) * exp(-ptrD * 0.5));
  q -= lean; // shift the sample coord → the body drifts toward the pointer
  // TOUCH — no warp. A soft, WIDE proximity field; petals near the cursor lift
  // gently (added at the composite). Cheap, and it never deforms the silhouette.
  float touchProx = uPointerIn > 0.01
    ? uTouch * exp(-dot(q - ptr, q - ptr) * 1.7) * 0.8
    : 0.0;

  float r = length(q);
  // early out — far outside everything (keeps fill-rate honest)
  if (r > extent * 1.15) { frag = vec4(0.0); return; }

  float th = atan(q.y, q.x);

  // BREATH + SWELL — inverse radial scale, so the whole body breathes
  float breathe = 1.0 + 0.018 * uBreath * sin(t * 0.5) + 0.04 * uSwell;
  r /= breathe;
  q /= breathe;

  // MEMORY GROWTH CURVE — fast when young, ever slower as the organism fills
  // out, but always still growing. Drives births AND the lifetime unfurl-spin.
  float aEff = 1.0 - pow(1.0 - uAge, 1.65);

  // SPIN — the standing rotation (slider + endless revolve + the lifetime
  // unfurl that growth winds up) turns the WHOLE body together; only the
  // MOTION is differential: while the spin is in flight, outer space trails
  // the velocity and whips back — inner petals stiff, outer ones carrying
  // the momentum. Never a standing shear, so petals stay petals.
  float spinAll = uSpin + aEff * 8.6;
  float trail = uSpinVel * 0.22 * smoothstep(0.1, 1.2, r);
  float thS = th - spinAll + trail;
  // WRAP to [-π, π] before thS feeds cos/sin AND the slot floor(). spinAll grows
  // without bound (the endless revolve accumulates forever), so thS becomes large
  // over time — and at large magnitudes float32 loses precision exactly at the
  // atan() branch cut (the −x axis). The two sides of the cut then round to petal
  // slots a hair apart, which reads as a hairline TEAR through the centre that
  // rotates with the spin (the CSS blur used to smear it; it shows once that's
  // gone). Wrapping keeps the angle small + precise; cos/sin are periodic so the
  // rendered body is identical. This is the one place the seam could form.
  thS -= TAU * floor(thS / TAU + 0.5);
  vec2 qs = vec2(cos(thS), sin(thS)) * r;

  // ── FORMATION — LAYERED WHORLS ─────────────────────────────────────────────
  // Each layer is a whorl of petals rooted near the cell; consecutive layers
  // rotate by phaseStep and reach further. Small step → spiral staircase arms ·
  // near-rational steps → twisted rosettes · GOLDEN → sunflower burst.
  // Radial culling per layer is EXACT (base + blade length are known), so no
  // petal is ever truncated by the candidate search.
  float N = max(uCount, 8.0);
  float layers = clamp(floor(3.0 + N / 36.0), 3.0, 7.0);
  float phaseStep = mix(0.26, GOLDEN, smoothstep(0.10, 0.60, uFormation));
  float baseLen = 0.34 + uPetalLen * 0.62;
  float spreadK = mix(0.4, 1.6, uRadialBias);      // whorl root stagger
  float lenFloor = mix(0.75, 0.34, uRadialBias);   // inner-vs-outer length contrast

  float sumW = 0.0;
  for (int L = 0; L < 7; L++) { if (float(L) < layers) sumW += 0.6 + float(L); }

  // colour breathes hardest at the heart — vibrance climbs toward the centre
  float centerW = 1.0 - smoothstep(0.10, 0.72, r / max(extent * 0.62, 1e-3));
  // PRESENCE — the body's own darkness (how far toward GLASS the petals go)
  // and the night it is kept through (see setPresence). At uPresence 0,
  // look == uDark and night == 0: every line below is the engine as it was.
  float look = uDark * (1.0 - uPresence);
  float night = uDark * uPresence;
  float satPush = 1.42 + centerW * 0.55;

  // WATERCOLOUR TRANSMITTANCE — the colour philosophy of the reference board:
  // each petal is a wash of pigment the light passes THROUGH. Stacked washes
  // MULTIPLY (order-independent), so overlaps pool deeper and more saturated
  // toward the pigment — never the grey mush of averaging.
  vec3  trans = vec3(1.0);
  float coverage = 0.0;
  float veinDark = 0.0;
  float edgePool = 0.0;
  vec3  light = vec3(0.0);
  vec3  nodeL = vec3(0.0);   // the node cells' own light, kept apart for the night
  vec3  veinL = vec3(0.0);

  // helix: at low formation each whorl RAMPS outward across its slots, so the
  // layers connect into one continuous spiral staircase (one turn per layer).
  float helix = 1.0 - smoothstep(0.10, 0.60, uFormation);
  float layerGap = rimA * spreadK / max(layers - 1.0, 1.0);

  float slotBase = 0.0;
  for (int L = 0; L < 7; L++) {
    if (float(L) >= layers) break;
    float fL = float(L);
    float ln = layers > 1.0 ? fL / (layers - 1.0) : 0.0;
    float mL = clamp(floor(N * (0.6 + fL) / sumW), 4.0, 15.0);
    // ROOT ORIGIN — pull every whorl's root in toward the core, well INSIDE the
    // cell rim (rimA), so the petal bases converge near the centre dot and hide
    // UNDER the cell instead of rooting out in the open (where their blunt bases
    // read as boxy stubs). The distance a root moved inward is added back as blade
    // length (rootPull), so the tips — and the whole silhouette/extent — land
    // exactly where they did; the flower just now fans smoothly from the core.
    float rootOld = rimA * (0.80 + ln * spreadK);
    float baseR = rimA * (0.24 + ln * 0.52);
    float rootPull = rootOld - baseR;
    float lenL = baseLen * mix(lenFloor, 1.0, pow(ln, 1.15)) + rootPull;
    // exact radial cull — this whorl cannot touch this pixel
    float lenMax = lenL * (1.0 + helix * 0.35);
    if (r > baseR + layerGap * helix + lenMax * 1.30 + 0.06 || r < baseR - lenL * 0.15 - 0.05) {
      slotBase += mL;
      continue;
    }
    float slotAng = TAU / mL;
    // INERTIA — outer layers trail the spin velocity and swing back (the inner
    // petals are stiff; the outer ones carry the momentum).
    float lag = -uSpinVel * (0.03 + 0.12 * ln);
    float phase = fL * phaseStep + lag
                + (hash11(fL * 17.3) - 0.5) * slotAng * 0.6 * uVariety;
    float kf = floor((thS - phase) / slotAng + 0.5);
    for (int a = -2; a <= 2; a++) {
      float ks = kf + float(a);
      float ksN = mod(ks, mL) / mL;
      float i = slotBase + mod(ks, mL);
      // BIRTH — memory fills the whorls from the heart outward, sweeping each
      // ring around; a petal only exists once the organism has grown past it.
      //
      // ⚠️ THE FIRST WHORL OPENS AS ONE. The ksN term is what sweeps a ring
      // around petal-by-petal, and on the INNERMOST ring that sweep is the
      // whole bloom — so for a moment a new creature is two or three lonely
      // leaves, which reads broken rather than young. The sweep is therefore
      // faded IN by ring: zero at the heart (the first whorl arrives whole),
      // full from the second ring out, where a sweep reads as growth.
      float sweep = smoothstep(0.0, 0.34, ln);
      // AGE ZERO IS A CELL. Every petal — the first whorl included — waits for
      // the organism to have grown past a small floor, so a bloom scrubbed to
      // zero (the birth page's resting cell) is one luminous cell and nothing
      // else, and the petals genuinely COME OUT of it as it is held. Natural
      // dashboard ages (≥0.12 → aEff ≥0.19) clear the floor at once, so no
      // seated organism changes.
      float birth = clamp(0.05 + ln * 0.72 + ksN * 0.20 * sweep, 0.0, 0.94);
      // the first whorl also opens a touch faster, so the heart is dressed
      // before the rings behind it start arriving
      float grow = smoothstep(birth, birth + mix(0.07, 0.12, sweep), aEff);
      if (grow <= 0.002) continue;
      float thI = phase + ks * slotAng;
      float baseRk = baseR + helix * ksN * layerGap;
      float lenK = lenL * (1.0 + helix * ksN * 0.35);
      Petal P = evalPetal(i, thI, baseRk, lenK, ln, grow, spinAll, qs, t);
      if (P.cov <= 0.001 && P.rim <= 0.001 && P.node <= 0.01) continue;
      // pigment keeps its hue spread but gets a saturation push (pale halo
      // stops vanish on eggshell and go grey on dark); light uses the full ramp.
      vec3 colPig = paletteRamp(P.hueT * mix(0.85, 1.0, look));
      float pigL = dot(colPig, vec3(0.299, 0.587, 0.114));
      colPig = clamp(mix(vec3(pigL), colPig, satPush) * 0.94, 0.0, 1.0);
      vec3 col = paletteRamp(P.hueT);
      // one wash of this petal's pigment — absorption ∝ its coverage; nearer
      // (younger, on-top) petals wash a little denser
      float dens = clamp(P.cov * 0.62 * (0.6 + 0.4 * P.depth), 0.0, 0.9);
      trans *= mix(vec3(1.0), colPig, dens);
      coverage = max(coverage, min(P.cov * 1.3, 1.0));
      veinDark = max(veinDark, min(P.vein, 1.0) * smoothstep(0.30, 0.70, P.cov));
      // edge pooling only where the blade fill is solid — never wiry outlines
      edgePool = max(edgePool, min(P.rim, 1.0) * smoothstep(0.30, 0.70, P.cov));
      // analytic light: glass rim + veins + node cells (bioluminescent language).
      // Veins are kept translucent — present, not dominant — and the membrane
      // body carries a soft glow of its own on dark grounds (petals read as
      // glass, not a bare fan of wires).
      light += col * P.rim * 0.5;
      vec3 vl = mix(col, uHalo, 0.45) * P.vein * (0.45 + 0.75 * uInnerLight);
      vec3 nl = mix(uGlow, col, 0.6) * P.node * (0.8 + 1.4 * uInnerLight);
      light += vl * (0.30 + 0.55 * look);
      light += nl;
      light += col * P.cov * look * (0.10 + 0.14 * uInnerLight);   // membrane self-glow
      nodeL += nl;
      veinL += vl * smoothstep(0.30, 0.70, P.cov);
    }
    slotBase += mL;
  }
  // the glass's own hue at full vividness — stained glass SHOWS its transmitted
  // colour (1-trans would be the absorbed complement — wrong hue). washDepth
  // grows as pigment stacks, so chroma deepens with density on any ground.
  float transMax = max(trans.r, max(trans.g, trans.b));
  vec3 pigVivid = transMax > 1e-3 ? clamp(trans / transMax, 0.0, 1.0) : uBody;
  float washDepth = clamp(1.0 - dot(trans, vec3(0.299, 0.587, 0.114)), 0.0, 1.0);

  // ── THE CELL — the brand pill, ERECT (it does not spin; the petals turn
  // around it), with a progressive focus: crisp at the top, dissolving into an
  // ethereal blur toward the bottom.
  float rimR = cellRadiusAt(th, t);
  float dc = r - rimR;
  // vertical focus ramp: 0 at the top of the cell → 1 at the bottom
  float defoc = smoothstep(0.55, -0.95, q.y / max(rimR, 1e-3));
  float cellIn = 1.0 - smoothstep(-0.006 - defoc * rimR * 0.08, 0.012 + defoc * rimR * 0.18, dc);
  // interior gradients are CIRCULAR (pill iso-contours read as a diamond
  // plateau); only the rim itself carries the pill silhouette.
  float rimBase = cellBase() * 1.06 * (1.0 + 0.03 * uBreath + 0.05 * uSwell);
  float rn = r / max(rimBase, 1e-3);                  // 0 centre → ~1 rim (circular)
  // pearl body: bright nucleus lifted slightly upward, glass limb-darkened rim
  vec2 nucC = vec2(0.0, rimBase * 0.18);
  float nuc = exp(-dot(q - nucC, q - nucC) / (rimBase * rimBase * (0.22 + defoc * 0.30)));
  // CELL TEMPERATURE LAW: the pearl never takes the palette's (or the ground's)
  // temperature — it stays consistently toward white, whatever the section mood.
  vec3 cellCol = mix(vec3(0.955, 0.955, 0.945), mix(uGlow, vec3(1.0), 0.9), nuc);
  cellCol = mix(cellCol, mix(uBody, vec3(1.0), 0.88), smoothstep(0.45, 1.0, rn) * 0.3 * (1.0 - defoc * 0.6));
  // limb darkening, lost in the blur below — SOFTENED on dark grounds, where a
  // darker rim reads as an inner shadow instead of glass (the pearl must stay
  // white on midnight; it may only be a touch less bright, never shadowed)
  cellCol *= 1.0 - 0.1 * (1.0 - uDark * 0.6) * smoothstep(0.6, 1.0, rn) * (1.0 - defoc) * (1.0 - uCellGlow);
  // THE ORB (uCellGlow): no limb at all — the pearl whitens out to its edge
  // brightest at its heart, a whisper of warmth toward its edge (light falls
  // off OUTWARD from the middle — never a bright rim round a duller centre)
  cellCol = mix(cellCol, mix(vec3(1.0), mix(uGlow, vec3(1.0), 0.9), smoothstep(0.35, 1.0, rn)) * mix(1.04, 0.97, smoothstep(0.0, 1.0, rn)), uCellGlow * 0.92);
  // on dark grounds the pearl lifts a LITTLE, still white-hearted (no colour
  // cast) — kept modest so the heart reads slightly dimmer than the light-ground
  // solar hull rather than blowing out against the midnight
  cellCol = mix(cellCol, mix(uGlow, vec3(1.0), 0.8 + 0.16 * nuc), uDark * 0.18);
  // the cell NOTICES the cursor — a soft, WIDE inner glow leans toward it
  // wherever it goes (the last known direction lingers when the pointer leaves)
  vec2 hotDir = normalize(ptr + vec2(1e-4, 0.20));
  float hot = exp(-length(q - hotDir * rimBase * 0.45) * 3.2 / max(rimBase, 1e-3) * 0.5);
  // never stack on the bright nucleus — the glow lives in the pearl's shade
  cellCol += mix(uGlow, vec3(1.0), 0.7) * hot * (0.05 + 0.1 * uPointerIn) * (1.0 - nuc * 0.75);
  // HOW LIT THE CORE IS (uCellLight). At 1 cellDim is exactly 0.0, so every
  // factor below is exactly 1.0 and the pearl, its halo and its birth glow are
  // today's, bit for bit. Falling, the pearl dims into a shaded bead: quieter
  // everywhere, and quieter still toward its limb (softened where the focus ramp
  // blurs the cell and on dark grounds, so no ring is drawn). A pure multiply:
  // the pearl keeps its temperature, it only loses light. Only the core answers
  // to this: petals, veins and the ground never read it.
  float cellDim = 1.0 - clamp(uCellLight, 0.0, 1.0);
  cellCol *= 1.0 - cellDim * (0.5 + 0.26 * smoothstep(0.3, 1.0, rn) * (1.0 - defoc * 0.6) * (1.0 - uDark * 0.6));
  // membrane rim light + a soft halo bleeding past the rim (seam safety);
  // both dissolve downward with the focus ramp
  // (an orb has no drawn rim: its edge is light, not a line)
  float rimLight = exp(-abs(dc) * (150.0 - defoc * 95.0)) * 0.32 * (1.0 - defoc * 0.4) * (1.0 - uCellGlow) * (1.0 - cellDim * 0.7);
  float orb = exp(-max(dc, 0.0) * (11.0 - defoc * 5.0) / max(rimR, 1e-3) * 0.7) * (1.0 - cellDim * 0.8);

  // ── THE BIRTH GLOW ────────────────────────────────────────────────────────
  // A creature arriving from nothing used to simply have petals appear. Now
  // the CELL announces it first: at low age the heart throws a wide, soft
  // light well past its own rim, which falls away as the whorls open over it.
  // One exponential on a value the shader already has (aEff), so it costs a
  // handful of ALU and nothing else - and because it rides age, EVERY surface
  // that grows a netti gets the same opening breath.
  float bornT = 1.0 - smoothstep(0.02, 0.42, aEff);
  float bornGlow = bornT * bornT * exp(-max(dc, 0.0) * 2.6 / max(rimR, 1e-3)) * 1.15 * (1.0 - cellDim * 0.85);

  // ── MOTES — a fine luminous dust, numerous and small, concentrated toward
  // the heart (radial distribution pulled inward, brighter near the centre).
  // uMoteSize sets grain (fine dust → soft orb).
  float moteL = 0.0;
  if (uMotes > 0.02) {
    float fall = mix(60000.0, 9000.0, uMoteSize);      // smaller default grain
    for (int m = 0; m < 40; m++) {
      float fm = float(m);
      float hm = hash11(fm * 7.77 + 3.3);
      float hm2 = hash11(fm * 3.13 + 1.9);
      // pow>1 pulls the distribution toward the centre (few stray to the fringe)
      float mr = 0.12 + pow(fract(hm * 13.7 + t * (0.010 + hm * 0.014)), 2.0)
               * (0.72 + petalMax * 0.2);
      float ma = hm2 * TAU + t * (0.05 + hm * 0.12) * (hm > 0.5 ? 1.0 : -1.0) + uSpin * 0.5;
      vec2 mp = vec2(cos(ma), sin(ma)) * mr;
      float md = length(qs - mp);
      float tw = 0.4 + 0.6 * pow(0.5 + 0.5 * sin(t * (2.2 + hm * 3.6) + fm * 1.7), 2.0);
      float cw = 1.0 - smoothstep(0.08, 0.85, mr);      // heart-concentrated glow
      moteL += exp(-md * md * fall) * tw * (0.45 + 0.9 * cw);
    }
    moteL *= uMotes * 0.85;
  }

  // TOUCH GLOW — the petals' inner light simply INTENSIFIES where the cursor
  // hovers: a smooth, steady lift (barely breathing), no high-frequency sparkle
  // — the shimmer strobed on some frames and read as a glitch.
  float scint = touchProx * coverage * (0.9 + 0.1 * sin(t * 2.2));

  // ── COMPOSITE — transparent, premultiplied; ground-aware pigment ─────────
  // LIGHT ground: the transmitted watercolour itself — the paper breathes
  // through thin washes, overlaps pool deep + saturated (the stone poster).
  // Veins and membrane edges pool MORE of the same pigment (multiply by the
  // transmittance again), so detail darkens toward colour, never toward grey.
  // DARK ground: the vivid pigment as deep stained glass, lit from within.
  float glassAlpha = mix(0.92, 0.74, uGlass) * mix(1.0, 0.62, look);
  // THE NIGHT PAPER (presence): the thinnest washes let a little of the ground
  // through, so the tips carry the room while the deep overlaps stay solid.
  float aPet = coverage * glassAlpha * mix(1.0, 0.7 + 0.3 * smoothstep(0.08, 0.5, washDepth), night);
  // the paper itself: white on a pale ground; kept through the night it takes
  // a luminous tint of the ground's own hue (the ground at full chroma, lifted
  // halfway to white), so the room's colour runs through every thin wash
  vec3 bgHue = uBg / max(max(uBg.r, max(uBg.g, uBg.b)), 1e-3);
  vec3 paper = mix(vec3(1.0), mix(bgHue, vec3(1.0), 0.52), night) * trans;
  paper *= mix(vec3(1.0), trans, veinDark * 0.85);
  paper *= mix(vec3(1.0), trans, edgePool * 0.55);
  // …and the deepest overlaps lift out of black, keeping their order
  paper = mix(paper, sqrt(max(paper, 0.0)), night * 0.3);
  vec3 darkBody = pigVivid * (0.16 + 0.5 * washDepth) * (1.0 + 0.35 * centerW);
  vec3 petBody = mix(paper, darkBody, look);
  // emissive gain on dark grounds; the heart burns brightest.
  // cursor glow is gentle on light grounds (it blew out on eggshell) and only
  // reaches full strength on dark, where emissive lift reads as luminosity.
  // Kept through the night, the node cells and the veins GLOW by the ground's
  // darkness: the cells stay the brightest detail on the body, never drowned.
  vec3 emis = light * (0.35 + 1.45 * look)
            + pigVivid * coverage * look * (0.28 + 0.4 * uInnerLight + 0.30 * centerW)
            + mix(pigVivid, uHalo, 0.5) * scint * (0.45 + 0.75 * uDark)   // cursor glow
            + (nodeL * 0.42 + veinL * 0.16) * night;

  // AN ORB IS ONLY LIGHT (uCellGlow): the petals folded under a resting cell
  // would show their deep body as a dark line round its rim; while the cell
  // glows they give way entirely, and the hold brings them back as it eases off
  aPet *= 1.0 - uCellGlow;
  emis *= 1.0 - uCellGlow;
  // …and what the rim blends toward is light too, not the petals' pigment
  petBody = mix(petBody, vec3(1.0), uCellGlow);

  vec3 colOut = petBody;
  float aOut = aPet;

  // white orb + cell over the petals (an opaque luminous heart)
  // the orb throws its light wider and brighter past the rim
  float orbA = orb * (1.0 - cellIn) * (0.5 + 0.35 * uCellGlow);
  colOut = mix(colOut, mix(vec3(1.0), uHalo, 0.25), clamp(orbA, 0.0, 1.0));
  aOut = max(aOut, orbA * 0.85);
  // On dark grounds the wide cellIn falloff band would blend the white pearl
  // toward the deep petal pigment while alpha stays pinned — painting a dark
  // ring INSIDE the silhouette (the "inner shadow"). Sharpen only the COLOUR
  // ramp there so the band reaches pearl-white well before the alpha edge; the
  // silhouette softness (alpha, below) is untouched, and uDark is exactly 0 on
  // light grounds so their look cannot change.
  float cellColIn = mix(cellIn, smoothstep(0.0, 0.55, cellIn), uDark);
  // an orb reaches white almost at once: no band where the pigment below shows
  cellColIn = mix(cellColIn, smoothstep(0.0, 0.25, cellIn), uCellGlow);
  colOut = mix(colOut, cellCol, cellColIn);
  aOut = max(aOut, cellIn * 0.96);
  // AN ORB HAS NO SEAM (uCellGlow): the pearl's alpha falls at its rim before
  // the halo's has risen, and on a dark ground that dip reads as a grey ring.
  // Glowing, the halo takes over continuously — white light from the pearl
  // straight out into its glow.
  colOut = mix(colOut, vec3(1.0), uCellGlow * (1.0 - cellIn));
  aOut = max(aOut, uCellGlow * clamp(orb, 0.0, 1.0) * mix(0.9, 0.0, smoothstep(0.0, 1.0, 1.0 - cellIn)) + uCellGlow * clamp(orb, 0.0, 1.0) * 0.62 * (1.0 - cellIn));

  // add the analytic light on top (knee-compressed — never blooms out).
  // The pearl masks the petal-light stack — roots under the cell must not
  // paint through it.
  vec3 lit = knee(emis + uHalo * rimLight * 0.5 + uGlow * moteL * 0.9 + vec3(1.0) * moteL * 0.35
                  // the birth burst joins the analytic light, so it is masked by
                  // the pearl exactly like every other emission and can never
                  // paint over the heart itself
                  + mix(uGlow, vec3(1.0), 0.55) * bornGlow);
  // (an orb is the light: glowing, the pearl no longer masks it, so there is
  // no seam where a masked glow meets an unmasked one)
  lit *= 1.0 - cellIn * 0.99 * (1.0 - uCellGlow);
  colOut += lit;
  aOut = max(aOut, clamp(max(lit.r, max(lit.g, lit.b)) * 0.9, 0.0, 1.0) * 0.85);
  // the outermost reaches fade into the ground — the organism has no hard end.
  // TWO ramps, and the second one is why: the first thins the outer reach to
  // 45% and then HOLDS it there, so every glow that still carries out to the
  // early-out radius (r > extent * 1.15) was guillotined at exactly 45% of
  // its alpha — a step, not an end. On an eggshell ground that step is faint
  // enough to pass; on a DARK one the emissive gain is ~5x (see emis), the
  // step lands around a third of full opacity, and netti stops being a bloom
  // and starts being a disc with a rim — and wherever that disc runs past the
  // canvas it is the canvas that ends it, so what a long-open dark dashboard
  // shows is a soft-edged BOX. The second ramp carries that held tail the rest
  // of the way to zero, closing it just inside the early-out so the cut removes
  // only pixels that are already nothing. Below u = 1.02 this multiplies by 1.0
  // — the body, the silhouette and the outer wash are bit-for-bit unchanged.
  float uEdge = r / max(extent * 0.92, 1e-3);
  aOut *= (1.0 - smoothstep(0.78, 1.04, uEdge) * 0.55) * (1.0 - smoothstep(1.02, 1.24, uEdge));
  aOut = clamp(aOut, 0.0, 1.0);

  // NEVER-CLIP II — the physical frame. The analytic fit keeps the body inside
  // the canvas at rest, but a FOCUS ZOOM (uViewZ > 1, the dive) magnifies it
  // inside the fixed frame — petals can reach the canvas border and shear on
  // it. Feather the last ~3% of the frame so anything that gets there MELTS
  // instead of cutting; at rest the fitted margin means this touches nothing.
  vec2 bo = vec2(0.5) - abs(vUv - 0.5);
  // THE MELT WIDENS WITH THE ZOOM. 3% is exactly the headroom the analytic fit
  // leaves at rest (the body lands at 0.47 of the half-frame), so it can't be
  // made wider for its own sake without eating the outermost reach. But it is
  // also, at rest, all the melt that is NEEDED — and at a zoom it is nowhere
  // near enough: magnified, the body arrives at the border still at full
  // strength, 3% of the frame melts a hairline, and what is left is a straight
  // bright edge on all four sides. That is the box. So the melt grows with the
  // magnification that causes it: unchanged at uViewZ = 1 (a no-op at rest, on
  // every surface), ~8% at 1.6, ~18% at 2.4. Whatever the zoom, netti ends by
  // dissolving rather than by running out of canvas.
  float melt = 0.03 * max(1.0, uViewZ * uViewZ);
  aOut *= smoothstep(0.0, melt, min(bo.x, bo.y));

  // final premultiplied output — the alpha IS the organism envelope
  frag = vec4(clamp(colOut, 0.0, 1.6) * aOut, aOut);
}
`;
